/**
 * Real bug found live: every clinical/financial write endpoint (and
 * Appointment's own Department selector — `DepartmentSelect.tsx`) requires
 * `currentBranch` from `useBranchStore`, but the ONLY way to set it was the
 * header's `HospitalBranchSwitcher` — a button labeled "Select hospital"
 * that doesn't read as a required form field, easy to miss entirely. A
 * single-hospital, single-branch tenant (the common case) had no reason to
 * ever be asked — this auto-selects Hospital/Branch on load when there's
 * exactly one of each, the same "don't make the user pick when there's
 * nothing to pick" pattern `listAllBranchesAcrossHospitals` was already
 * built for. A genuinely multi-hospital/multi-branch tenant is untouched:
 * the picker still works exactly as before whenever a real choice exists.
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listHospitals, listBranches } from '@services/platform.service';
import { useHospitalStore } from '@store/hospital.store';
import { useBranchStore } from '@store/branch.store';

export function useAutoSelectSingleHospitalBranch(): void {
  const currentHospital = useHospitalStore((state) => state.currentHospital);
  const setHospital = useHospitalStore((state) => state.setHospital);
  const currentBranch = useBranchStore((state) => state.currentBranch);
  const setBranch = useBranchStore((state) => state.setBranch);

  const { data: hospitals } = useQuery({
    queryKey: ['auto-select', 'hospitals'],
    queryFn: listHospitals,
    enabled: !currentHospital,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (!currentHospital && hospitals?.length === 1) {
      const hospital = hospitals[0];
      setHospital({ id: hospital.id, legalName: hospital.legalName, displayName: hospital.displayName });
    }
  }, [currentHospital, hospitals, setHospital]);

  const effectiveHospitalId = currentHospital?.id ?? (hospitals?.length === 1 ? hospitals[0].id : undefined);

  const { data: branches } = useQuery({
    queryKey: ['auto-select', 'branches', effectiveHospitalId],
    queryFn: () => listBranches(effectiveHospitalId as string),
    enabled: !currentBranch && Boolean(effectiveHospitalId),
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (!currentBranch && branches?.length === 1) {
      const branch = branches[0];
      setBranch({ id: branch.id, code: branch.code, name: branch.name });
    }
  }, [currentBranch, branches, setBranch]);
}
